import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FlaskConical,
  Droplets,
  Waves,
  ShieldCheck,
  ArrowRight,
  Check,
  Blocks,
  ShieldAlert,
  MonitorCog,
  Mail,
  ChevronDown,
} from "lucide-react";

const heroImage =
  "/Images/services/oil-gas/completion-fluids/completion_02.webp";

const experienceImage =
  "/Images/services/oil-gas/completion-fluids/completion.webp";

const technologies = [
  {
    title: "GS-BRINE CF",
    subtitle: "Sistemas de Salmueras de Completamiento",
    icon: Droplets,
    description:
      "Sistemas diseñados mediante mezclas de salmueras monovalentes y divalentes para alcanzar la densidad requerida sin presencia de sólidos.",
    items: [
      "Compatibilidad química con el reservorio",
      "Excelente estabilidad del pozo",
      "Sistemas libres de sólidos",
      "Adaptación a cada operación",
      "Diseño bajo estándares HSE",
    ],
  },

  {
    title: "G-BREAKER",
    subtitle: "Tecnología inteligente para remoción de revoques",
    icon: FlaskConical,
    description:
      "Sistema desarrollado por Sierra Alta para remover eficientemente los revoques de filtración protegiendo la formación productora.",
    items: [
      "Tecnología propia",
      "Mayor eficiencia de limpieza",
      "Protección de la formación",
      "Diseño específico por campo",
      "Compatible con diferentes fluidos",
    ],
  },

  {
    title: "Interfase Fluido - Fluido",
    subtitle: "Optimización de la movilidad del petróleo",
    icon: Waves,
    description:
      "Evaluación de tensión interfacial mediante equipos especializados para desarrollar fluidos que favorezcan la recuperación de hidrocarburos.",
    items: [
      "Reducción de tensión interfacial",
      "Mejor mojabilidad al agua",
      "Mayor movilidad del petróleo",
      "Protección del reservorio",
    ],
  },
];

const technologies_completion = [
  {
    title: "G-DRILL RDF PLUS",
    subtitle: "Sistema Drill-In para perforación y estimulación",
    icon: FlaskConical,
    description:
      "Sistema de Drill-In que combina la perforación con la estimulación del yacimiento, incorporando agentes de puenteo acidificables y surfactantes para optimizar las condiciones de la formación.",
    items: [
      "Agentes de puenteo acidificables",
      "Reducción de la tensión interfacial",
      "Favorece la mojabilidad al agua de la roca",
      "Previene la generación de incrustaciones inorgánicas",
      "Compatibilidad con fluidos de formación",
      "Implementado con éxito en campos como Infantas Norte",
    ],
  },

  {
    title: "G-MAX RDF",
    subtitle: "Fluido de completamiento y perforación de yacimientos",
    icon: Droplets,
    description:
      "Fluido de completamiento y perforación libre de barita, diseñado para alcanzar altas densidades mediante el uso de sales de formiato y densificantes avanzados.",
    items: [
      "Densidades de hasta 20 ppg",
      "Libre de barita",
      "Compatible con sales monovalentes y divalentes",
      "Revoque altamente removible",
      "Hasta 90.3% de remoción con G-BREAKER MAX",
      "Ideal para nuevas completaciones y reintervenciones",
      "Aplicable en profundizaciones de pozos",
    ],
  },

  {
    title: "GS-BRINE CF",
    subtitle: "Sistema de salmueras de completamiento",
    icon: Waves,
    description:
      "Sistema de salmueras de completamiento adaptable a las características de cada reservorio, diseñado para garantizar una terminación eficiente bajo altos estándares de seguridad y desempeño.",
    items: [
      "Adaptación a las condiciones de cada reservorio",
      "Sistemas de salmueras monovalentes y divalentes",
      "Diseñado bajo altos estándares HSE",
      "Terminaciones eficientes y confiables",
      "Operaciones seguras y controladas",
    ],
  },
];

const technologies_loss_control = [
  {
    title: "G-BRIDGE",
    subtitle: "Materiales de puenteo para un sellado efectivo",
    icon: Blocks,
    description:
      "Materiales de puenteo disponibles en diferentes granulometrías, diseñados para minimizar el daño a la formación y lograr un sellado efectivo de acuerdo con las características específicas de cada pozo.",
    items: [
      "Granulometrías gruesa, media y fina",
      "Aplicación directamente en el fluido o mediante sistemas de píldoras",
      "Diseñado para minimizar el daño a la formación",
      "Soluble en tratamientos de remoción con G-BREAKER",
      "Simulaciones mediante software WEL BRIDGE",
      "Determinación del tamaño óptimo de partícula para un sellado efectivo",
    ],
  },

  {
    title: "WEL SURE",
    subtitle: "Sistema para el control de pérdidas severas de circulación",
    icon: ShieldAlert,
    description:
      "Sistema diseñado específicamente para controlar pérdidas severas de circulación en sobrecarga y formaciones productoras, ayudando a prevenir eventos asociados con la inestabilidad del pozo y problemas de control.",
    items: [
      "Diseñado para pérdidas severas de circulación",
      "Aplicable en sobrecarga y formaciones productoras",
      "Ayuda a prevenir problemas de inestabilidad del pozo",
      "Contribuye al control de eventos críticos durante la operación",
      "Considera el tiempo disponible para las operaciones",
      "Adaptable a las restricciones de concentración de las herramientas",
      "Considera las temperaturas de operación y del yacimiento",
      "Diseñado de acuerdo con el ancho de la fractura",
      "Probado en escenarios extremos de pérdidas de circulación",
      "Caso exitoso de control de pérdidas con una píldora de 20 bbl en Kurdistán, Irak",
    ],
  },

  {
    title: "G-THIX PLUG",
    subtitle: "Sistema tixotrópico para el control de pérdidas",
    icon: FlaskConical,
    description:
      "Sistema tixotrópico diseñado para el control de pérdidas de circulación. Su capacidad de gelificación al detener el bombeo permite sellar fracturas de manera efectiva y formar un tapón temporal con baja invasión a la formación.",
    items: [
      "Gelificación al detener el bombeo",
      "Sellado efectivo de fracturas",
      "Baja concentración de material particulado",
      "Alta tolerancia a presiones diferenciales",
      "Aplicable en zonas con fracturamiento inducido",
      "Fácil circulación durante el bombeo",
      "Forma un tapón temporal para el control de pérdidas",
      "Bajo radio de invasión en la formación",
    ],
  },
];

const CompletionFluids = () => {
  const [openTechnology, setOpenTechnology] = useState(null);

  const [openCompletion, setOpenCompletion] = useState(null);

  const [openLossControl, setOpenLossControl] = useState(null);
  return (
    <>
      {/* HERO */}
      <section className="overflow-hidden">
        <div
          className="
      relative
      h-[520px]
      sm:h-[500px]
      md:h-[560px]
      lg:h-[580px]
      xl:h-[600px]
    "
        >
          {/* IMAGEN */}
          <img
            src={heroImage}
            alt="Fluidos de completamiento"
            className="
        absolute
        inset-0
        w-full
        h-full
        object-cover
        object-[center_10%]
        sm:object-[center_10%]
        md:object-[center_15%]
        lg:object-[center_15%]
      "
          />

          <div className="absolute inset-0 bg-black/20" />

          <div
            className="
        absolute
        inset-0
        bg-gradient-to-r
        from-black/65
        via-black/35
        to-transparent
      "
          />

          <div
            className="
        absolute
        inset-0
        flex
        items-center
      "
          >
            <div
              className="
          w-full
          max-w-7xl
          mx-auto
          px-4
          sm:px-6
          lg:px-8
        "
            >
              <div
                className="
            max-w-3xl
            pt-10
            sm:pt-12
            md:pt-14
          "
              >
                <span
                  className="
              inline-flex
              items-center
              px-3
              py-1.5
              sm:px-4
              sm:py-2
              rounded-full
              bg-[#ee7d32]/20
              text-[#ee7d32]
              text-xs
              sm:text-sm
              font-semibold
              uppercase
              tracking-wider
            "
                >
                  Servicios
                </span>

                <h1
                  className="
              mt-4
              sm:mt-5
              md:mt-6
              text-4xl
              sm:text-5xl
              md:text-6xl
              lg:text-7xl
              font-bold
              text-white
              leading-[0.95]
              tracking-tight
            "
                >
                  Fluidos de
                  <br />
                  <span className="text-[#ee7d32]">Completamiento</span>
                </h1>

                <p
                  className="
              mt-4
              sm:mt-5
              md:mt-6
              text-base
              sm:text-lg
              md:text-xl
              text-white/90
              leading-7
              md:leading-8
              max-w-2xl
            "
                >
                  Ingeniería avanzada para maximizar la productividad del
                  yacimiento.
                </p>

                <p
                  className="
              mt-3
              sm:mt-4
              md:mt-5
              text-sm
              sm:text-base
              text-white/80
              leading-6
              sm:leading-7
              max-w-2xl
            "
                >
                  En Sierra Alta desarrollamos soluciones especializadas en
                  fluidos de completamiento diseñadas para proteger la formación
                  productora, optimizar la movilidad de hidrocarburos y
                  garantizar operaciones seguras.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPERIENCIA */}
      <section className="py-20 md:py-28 bg-[#f3f3f3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="overflow-hidden rounded-[40px] rounded-tr-[100px] rounded-bl-[100px] shadow-xl">
                <img
                  src={experienceImage}
                  alt="Ingeniería de fluidos"
                  className="w-full h-[350px] md:h-[520px] object-cover hover:scale-105 transition duration-700"
                />
              </div>
            </div>

            <div>
              <div className="w-14 h-1 bg-[#ee7d32] rounded-full mb-6" />

              <h2 className="text-4xl md:text-5xl font-bold leading-tight text-[#111]">
                Ingeniería especializada para
                <span className="text-[#ee7d32]"> cada operación</span>
              </h2>

              <p className="mt-6 text-[#444] text-lg leading-8">
                Cada reservorio presenta condiciones únicas. Por ello nuestros
                fluidos de completamiento no son productos estándar; cada
                sistema es diseñado mediante estudios de laboratorio,
                simulaciones hidráulicas y análisis de compatibilidad.
              </p>

              <div className="mt-8 bg-white rounded-[30px] p-7 shadow-sm">
                <div className="flex gap-4">
                  <ShieldCheck size={65} stroke="#ee7d32" />

                  <div>
                    <p className="font-bold text-lg">
                      Soluciones diseñadas para proteger el yacimiento
                    </p>

                    <p className="mt-2 text-gray-600 leading-7">
                      Analizamos la interacción entre reservorio, fluido y
                      condiciones operacionales para garantizar una terminación
                      eficiente y segura.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TECNOLOGIAS */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="w-14 h-1 bg-[#ee7d32] rounded-full mb-6" />

            <h2 className="text-4xl md:text-5xl font-bold">
              Tecnologías que
              <span className="text-[#ee7d32]"> protegen el yacimiento</span>
            </h2>

            <p className="mt-5 text-gray-600 text-lg">
              Sistemas desarrollados para maximizar desempeño, seguridad y
              compatibilidad durante las operaciones de completamiento.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-7 mt-16">
            {technologies.map((item, index) => {
              const Icon = item.icon;
              const isOpen = openTechnology === index;

              return (
                <div
                  key={index}
                  className="
          group
          relative
          flex
          flex-col
          h-full
          bg-white
          rounded-3xl
          px-7
          pt-14
          pb-8
          shadow-[0_20px_60px_rgba(0,0,0,0.06)]
          transition-all
          duration-500
          hover:-translate-y-2
          hover:shadow-[0_30px_80px_rgba(243,112,33,0.16)]
        "
                >
                  <div
                    className="
            absolute
            -top-9
            left-7
            w-[72px]
            h-[72px]
            rounded-full
            bg-[#ee7d32]
            flex
            items-center
            justify-center
            shadow-[0_12px_30px_rgba(238,125,50,0.25)]
            transition-transform
            duration-300
            group-hover:scale-110
          "
                  >
                    <Icon className="w-8 h-8" stroke="white" strokeWidth={2} />
                  </div>

                  <div className="flex flex-col flex-1">
                    <h3
                      className="
              text-xl
              sm:text-[22px]
              font-extrabold
              text-[#111]
              leading-tight
              pr-10
            "
                    >
                      {item.title}
                    </h3>

                    <p
                      className="
              mt-2
              text-sm
              font-semibold
              text-[#ee7d32]
            "
                    >
                      {item.subtitle}
                    </p>

                    <p
                      className="
              mt-5
              text-sm
              text-gray-600
              leading-7
            "
                    >
                      {item.description}
                    </p>

                    <div className="my-6 h-px bg-gray-100" />

                    <div className="mt-auto pt-6 flex justify-end">
                      <button
                        type="button"
                        onClick={() => setOpenTechnology(isOpen ? null : index)}
                        className="
                group/details
                inline-flex
                items-center
                gap-2
                text-sm
                font-bold
                transition-all
                duration-300
              "
                      >
                        <span
                          className="
                  text-[#666666]
                  group-hover/details:text-[#ee7d32]
                  transition-colors
                  duration-300
                "
                        >
                          {isOpen ? "Ocultar detalles" : "Conocer más"}
                        </span>

                        <ChevronDown
                          className={`
                  w-4
                  h-4
                  stroke-[#666666]
                  group-hover/details:stroke-[#ee7d32]
                  transition-all
                  duration-300
                  ${isOpen ? "rotate-180 stroke-[#ee7d32]" : ""}
                `}
                          strokeWidth={2}
                        />
                      </button>
                    </div>
                  </div>

                  <div
                    className={`
            overflow-hidden
            transition-all
            duration-500
            ease-in-out
            ${
              isOpen
                ? "max-h-[400px] opacity-100 mt-6"
                : "max-h-0 opacity-0 mt-0"
            }
          `}
                  >
                    <div className="space-y-3 border-t border-gray-100 pt-6">
                      {item.items.map((x, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div
                            className="
                    mt-1
                    w-5
                    h-5
                    rounded-full
                    bg-[#ee7d32]/10
                    flex
                    items-center
                    justify-center
                    shrink-0
                  "
                          >
                            <svg
                              viewBox="0 0 20 20"
                              fill="none"
                              className="w-3 h-3"
                            >
                              <path
                                d="M5 10.5L8.5 14L15 6.5"
                                stroke="#ee7d32"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>

                          <span
                            className="
                    text-sm
                    text-gray-600
                    leading-6
                  "
                          >
                            {x}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SISTEMAS Y TECNOLOGÍAS CLAVE PARA COMPLETAMIENTO */}
      <section className="relative py-20 md:py-24 bg-[#f3f3f3] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="w-14 h-[4px] bg-[#ee7d32] rounded-full mb-5" />

            <h2 className="mt-6 text-4xl sm:text-5xl md:text-6xl font-bold text-[#111] leading-[1]">
              Sistemas y Tecnologías{" "}
              <span className="text-[#ee7d32]">Clave para Completamiento</span>
            </h2>

            <p className="mt-6 text-base sm:text-lg text-[#555] leading-8 max-w-2xl">
              Nuestro portafolio incluye sistemas avanzados que aseguran el
              éxito en la etapa de completamiento, proporcionando soluciones
              especializadas para optimizar el desempeño de los pozos y las
              condiciones del reservorio.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-7 mt-16">
            {technologies_completion.map((item, index) => {
              const Icon = item.icon;
              const isOpen = openCompletion === index;

              return (
                <div
                  key={index}
                  className={`
          group
          relative
          flex
          flex-col
          h-full
          bg-white
          rounded-3xl
          px-7
          pt-14
          pb-7
          border
          border-gray-100
          transition-all
          duration-500
          ${
            isOpen
              ? "shadow-[0_25px_70px_rgba(238,125,50,0.12)]"
              : "shadow-[0_20px_60px_rgba(0,0,0,0.06)] hover:-translate-y-2 hover:shadow-[0_30px_80px_rgba(238,125,50,0.14)]"
          }
        `}
                >
                  <div
                    className="
            absolute
            -top-9
            left-7
            w-[72px]
            h-[72px]
            rounded-full
            bg-[#ee7d32]
            flex
            items-center
            justify-center
            shadow-[0_12px_30px_rgba(238,125,50,0.25)]
            transition-all
            duration-500
            group-hover:scale-110
            group-hover:shadow-[0_16px_35px_rgba(238,125,50,0.35)]
          "
                  >
                    <Icon className="w-8 h-8" stroke="white" strokeWidth={2} />
                  </div>

                  <div className="flex flex-col flex-1">
                    <h3
                      className="
              text-xl
              sm:text-[22px]
              font-extrabold
              text-[#111]
              leading-tight
              pr-10
            "
                    >
                      {item.title}
                    </h3>

                    <p
                      className="
              mt-2
              text-sm
              font-semibold
              text-[#ee7d32]
            "
                    >
                      {item.subtitle}
                    </p>

                    <p
                      className="
              mt-5
              text-sm
              text-gray-600
              leading-7
            "
                    >
                      {item.description}
                    </p>

                    <div className="mt-auto pt-6 flex justify-end">
                      <button
                        type="button"
                        onClick={() => setOpenCompletion(isOpen ? null : index)}
                        className="
                group/details
                inline-flex
                items-center
                gap-2
                text-sm
                font-bold
                transition-all
                duration-300
              "
                      >
                        <span
                          className="
                  text-[#666666]
                  transition-colors
                  duration-300
                  group-hover/details:text-[#ee7d32]
                "
                        >
                          {isOpen ? "Ocultar detalles" : "Conocer más"}
                        </span>

                        <ChevronDown
                          className={`
                  w-4
                  h-4
                  stroke-[#666666]
                  transition-all
                  duration-300
                  group-hover/details:stroke-[#ee7d32]
                  ${isOpen ? "rotate-180 stroke-[#ee7d32]" : ""}
                `}
                          strokeWidth={2.5}
                        />
                      </button>
                    </div>
                  </div>

                  <div
                    className={`
            overflow-hidden
            transition-all
            duration-500
            ease-in-out
            ${
              isOpen
                ? "max-h-[450px] opacity-100 mt-6"
                : "max-h-0 opacity-0 mt-0"
            }
          `}
                  >
                    <div className="border-t border-gray-100 pt-6">
                      <p
                        className="
                mb-5
                text-[10px]
                font-bold
                uppercase
                tracking-[0.16em]
                text-gray-400
              "
                      >
                        Características
                      </p>

                      <div className="space-y-3">
                        {item.items.map((x, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <div
                              className="
                      mt-1
                      w-5
                      h-5
                      rounded-full
                      bg-[#ee7d32]/10
                      flex
                      items-center
                      justify-center
                      shrink-0
                    "
                            >
                              <Check
                                className="w-3 h-3"
                                stroke="#ee7d32"
                                strokeWidth={3}
                              />
                            </div>

                            <span
                              className="
                      text-sm
                      text-gray-600
                      leading-6
                    "
                            >
                              {x}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ADITIVOS ESPECIALIZADOS Y AGENTES DE CONTROL DE PÉRDIDAS */}
      <section className="relative py-20 md:py-24 bg-white overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="w-14 h-[4px] bg-[#ee7d32] rounded-full mb-5" />

            <h2 className="mt-6 text-4xl sm:text-5xl md:text-6xl font-bold text-[#111] leading-[1]">
              Aditivos Especializados y{" "}
              <span className="text-[#ee7d32]">
                Agentes de Control de Pérdidas
              </span>
            </h2>

            <p className="mt-6 text-base sm:text-lg text-[#555] leading-8 max-w-2xl">
              Para resolver los desafíos específicos de cada pozo, disponemos de
              aditivos y sistemas de alto rendimiento diseñados para optimizar
              el control de pérdidas, minimizar el daño a la formación y
              mantener la estabilidad e integridad del pozo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-7 mt-16">
            {technologies_loss_control.map((item, index) => {
              const Icon = item.icon;
              const isOpen = openLossControl === index;

              return (
                <div
                  key={index}
                  className={`
          group
          relative
          bg-white
          rounded-3xl
          px-7
          pt-14
          pb-7
          border
          border-gray-100
          flex
          flex-col
          h-full
          transition-all
          duration-500
          ${
            isOpen
              ? "shadow-[0_25px_70px_rgba(238,125,50,0.12)]"
              : "shadow-[0_20px_60px_rgba(0,0,0,0.06)] hover:-translate-y-2 hover:shadow-[0_30px_80px_rgba(238,125,50,0.14)]"
          }
        `}
                >
                  <div
                    className="
            absolute
            -top-9
            left-7
            w-[72px]
            h-[72px]
            rounded-full
            bg-[#ee7d32]
            flex
            items-center
            justify-center
            shadow-[0_12px_30px_rgba(238,125,50,0.25)]
            transition-all
            duration-500
            group-hover:scale-110
            group-hover:shadow-[0_16px_35px_rgba(238,125,50,0.35)]
          "
                  >
                    <Icon className="w-8 h-8" stroke="white" strokeWidth={2} />
                  </div>

                  <div className="flex flex-col flex-1">
                    <h3
                      className="
              text-xl
              sm:text-[22px]
              font-extrabold
              text-[#111]
              leading-tight
              pr-10
            "
                    >
                      {item.title}
                    </h3>

                    <p
                      className="
              mt-2
              text-sm
              font-semibold
              text-[#ee7d32]
            "
                    >
                      {item.subtitle}
                    </p>

                    <p
                      className="
              mt-5
              text-sm
              text-gray-600
              leading-7
            "
                    >
                      {item.description}
                    </p>

                    <div className="mt-auto pt-6 flex justify-end">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenLossControl(isOpen ? null : index)
                        }
                        className="
                group/details
                inline-flex
                items-center
                gap-2
                text-sm
                font-bold
                transition-all
                duration-300
              "
                      >
                        <span
                          className="
                  text-[#666666]
                  transition-colors
                  duration-300
                  group-hover/details:text-[#ee7d32]
                "
                        >
                          {isOpen ? "Ocultar detalles" : "Conocer más"}
                        </span>

                        <ChevronDown
                          className={`
                  w-4
                  h-4
                  stroke-[#666666]
                  transition-all
                  duration-300
                  group-hover/details:stroke-[#ee7d32]
                  ${isOpen ? "rotate-180 stroke-[#ee7d32]" : ""}
                `}
                          strokeWidth={2.5}
                        />
                      </button>
                    </div>
                  </div>

                  <div
                    className={`
            overflow-hidden
            transition-all
            duration-500
            ease-in-out
            ${isOpen ? "max-h-[600px] opacity-100 mt-6" : "max-h-0 opacity-0"}
          `}
                  >
                    <div className="border-t border-gray-100 pt-6">
                      <p
                        className="
                mb-5
                text-[10px]
                font-bold
                uppercase
                tracking-[0.16em]
                text-gray-400
              "
                      >
                        Características
                      </p>

                      <div className="space-y-3">
                        {item.items.map((x, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <div
                              className="
                      mt-1
                      w-5
                      h-5
                      rounded-full
                      bg-[#ee7d32]/10
                      flex
                      items-center
                      justify-center
                      shrink-0
                    "
                            >
                              <Check
                                className="w-3 h-3"
                                stroke="#ee7d32"
                                strokeWidth={3}
                              />
                            </div>

                            <span
                              className="
                      text-sm
                      text-gray-600
                      leading-6
                    "
                            >
                              {x}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SOPORTE TÉCNICO Y DE LABORATORIO */}
      <section className="relative py-20 md:py-24 bg-[#f3f3f3] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="w-14 h-[4px] bg-[#ee7d32] rounded-full mb-5" />

            <h2 className="mt-6 text-4xl sm:text-5xl md:text-6xl font-bold text-[#111] leading-[1]">
              Soporte Técnico y{" "}
              <span className="text-[#ee7d32]">de Laboratorio</span>
            </h2>

            <p className="mt-6 text-base sm:text-lg text-[#555] leading-8 max-w-2xl">
              Nuestro soporte se extiende más allá de la formulación de fluidos,
              integrando herramientas de simulación, diseño predictivo y pruebas
              especializadas de laboratorio para garantizar la calidad y el
              desempeño de cada sistema.
            </p>
          </div>

          <div className="relative mt-16 md:mt-20">
            <div
              className="
          hidden
          md:block
          absolute
          left-[36px]
          top-10
          bottom-10
          w-px
          bg-gradient-to-b
          from-[#ee7d32]
          via-[#ee7d32]/30
          to-transparent
        "
            />

            <div className="space-y-12 md:space-y-16">
              <div className="relative flex flex-col md:flex-row gap-8 md:gap-12">
                <div className="relative z-10 shrink-0">
                  <div
                    className="
                w-[72px]
                h-[72px]
                rounded-full
                bg-[#ee7d32]
                flex
                items-center
                justify-center
                shadow-[0_12px_30px_rgba(238,125,50,0.25)]
              "
                  >
                    <span className="text-xl font-extrabold text-white">
                      01
                    </span>
                  </div>
                </div>

                <div
                  className="
              flex-1
              md:pr-12
              pb-12
              md:pb-16
              border-b
              border-gray-100
            "
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                    <div
                      className="
                  w-14
                  h-14
                  rounded-2xl
                  bg-[#fff3eb]
                  text-[#ee7d32]
                  flex
                  items-center
                  justify-center
                  shrink-0
                   shadow-[0_8px_25px_rgba(238,125,50,0.18)]
                "
                    >
                      <MonitorCog
                        className="w-7 h-7"
                        stroke="#ee7d32"
                        strokeWidth={1.8}
                      />
                    </div>

                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#ee7d32]">
                        Ingeniería y simulación
                      </p>

                      <h3 className="mt-2 text-2xl sm:text-3xl font-extrabold text-[#111]">
                        Simulación y Diseño Predictivo
                      </h3>
                    </div>
                  </div>

                  <p className="mt-6 text-base text-gray-600 leading-8 max-w-4xl">
                    Utilizamos software especializado para analizar y diseñar
                    sistemas de fluidos con precisión, permitiendo anticipar su
                    comportamiento y optimizar su desempeño antes de su
                    aplicación en campo.
                  </p>

                  <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div
                      className="
                  p-5
                  rounded-2xl
                  bg-[#f8f7f4]
                  border
                  border-gray-100
                  transition-all
                  duration-300
                  hover:border-[#ee7d32]/30
                "
                    >
                      <p className="text-sm font-extrabold text-[#111]">
                        ONE SYNC
                      </p>

                      <p className="mt-2 text-sm text-gray-600 leading-6">
                        Simulador hidráulico utilizado para analizar y optimizar
                        el comportamiento de los sistemas de fluidos.
                      </p>
                    </div>

                    <div
                      className="
                  p-5
                  rounded-2xl
                  bg-[#f8f7f4]
                  border
                  border-gray-100
                  transition-all
                  duration-300
                  hover:border-[#ee7d32]/30
                "
                    >
                      <p className="text-sm font-extrabold text-[#111]">
                        BEST-3 & BEST-BRIDGE
                      </p>

                      <p className="mt-2 text-sm text-gray-600 leading-6">
                        Herramientas para diseñar la distribución óptima de
                        partículas (PSD) y mejorar la eficiencia del puenteo.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div
                      className="
      relative
      w-5
      h-5
      flex
      items-center
      justify-center
      shrink-0
    "
                    >
                      {/* Anillo animado */}
                      <span
                        className="
        absolute
        w-4
        h-4
        rounded-full
        bg-[#ee7d32]/30
        animate-ping
      "
                      />

                      {/* Punto */}
                      <span
                        className="
        relative
        w-2.5
        h-2.5
        rounded-full
        bg-[#ee7d32]
      "
                      />
                    </div>

                    <p className="text-sm font-medium text-gray-600 leading-6">
                      Diseño de fluidos con mayor precisión y capacidad
                      predictiva.
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative flex flex-col md:flex-row gap-8 md:gap-12">
                <div className="relative z-10 shrink-0">
                  <div
                    className="
                w-[72px]
                h-[72px]
                rounded-full
                bg-[#ee7d32]
                flex
                items-center
                justify-center
                shadow-[0_12px_30px_rgba(0,0,0,0.15)]
              "
                  >
                    <span className="text-xl font-extrabold text-white">
                      02
                    </span>
                  </div>
                </div>

                <div className="flex-1 md:pr-12">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                    <div
                      className="
                  w-14
                  h-14
                  rounded-2xl
                  bg-[#fff3eb]
                  text-white
                  flex
                  items-center
                  justify-center
                  shrink-0
                            shadow-[0_8px_25px_rgba(238,125,50,0.18)]
                "
                    >
                      <FlaskConical
                        className="w-7 h-7"
                        stroke="#ee7d32"
                        strokeWidth={1.8}
                      />
                    </div>

                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#ee7d32]">
                        Análisis y validación
                      </p>

                      <h3 className="mt-2 text-2xl sm:text-3xl font-extrabold text-[#111]">
                        Pruebas de Laboratorio Especializadas
                      </h3>
                    </div>
                  </div>

                  <p className="mt-6 text-base text-gray-600 leading-8 max-w-4xl">
                    Nuestros laboratorios cuentan con equipos especializados
                    para realizar pruebas avanzadas que permiten evaluar,
                    validar y garantizar la calidad y el desempeño de cada
                    sistema antes y durante su aplicación.
                  </p>

                  <div className="mt-8 flex flex-wrap gap-3">
                    {[
                      "Reología HTHP",
                      "Hasta 500°F",
                      "Hasta 20,000 psi",
                      "Tensiometría de gota rotatoria",
                      "Grace M6500",
                      "Permeámetro",
                      "Filtración dinámica",
                      "Pruebas de compatibilidad",
                      "Evaluación de puenteo (PPT)",
                    ].map((item, index) => (
                      <span
                        key={index}
                        className="
                    inline-flex
                    items-center
                    px-4
                    py-2.5
                    rounded-full
                    bg-[#f8f7f4]
                    border
                    border-gray-200
                    text-sm
                    font-medium
                    text-gray-600
                    transition-all
                    duration-300
                    hover:border-[#ee7d32]/40
                    hover:text-[#ee7d32]
                  "
                      >
                        {item}
                      </span>
                    ))}
                  </div>

                  <div className="mt-8 flex items-start gap-3">
                    <div
                      className="
      relative
      mt-[5px]
      w-5
      h-5
      flex
      items-center
      justify-center
      shrink-0
    "
                    >
                      <span
                        className="
        absolute
        w-4
        h-4
        rounded-full
        bg-[#ee7d32]/30
        animate-ping
      "
                      />

                      <span
                        className="
        relative
        w-2.5
        h-2.5
        rounded-full
        bg-[#ee7d32]
      "
                      />
                    </div>

                    <p className="text-sm font-medium text-gray-600 leading-6">
                      Validación técnica y control de calidad para garantizar el
                      desempeño de cada sistema.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FLUIDOS DE COMPLETAMIENTO */}
      <section className="relative py-10 md:py-10 bg-white overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="group relative overflow-hidden rounded-[30px] border border-[#ececec] bg-white shadow-[0_12px_40px_rgba(0,0,0,0.05)] transition-all duration-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.10)]">
            <div className="absolute top-0 left-0 h-full w-1.5 bg-gradient-to-b from-[#ee7d32] via-[#ee7d32]/50 to-transparent" />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10 px-6 py-8 md:px-10 md:py-10">
              <div className="flex flex-col sm:flex-row items-start gap-6 max-w-3xl">
                <div className="relative flex-shrink-0">
                  <div className="absolute inset-0 bg-[#ee7d32]/20 blur-xl rounded-2xl" />

                  <div className="relative w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center">
                    <Mail
                      size={40}
                      className="stroke-[#ee7d32]"
                      strokeWidth={1.8}
                    />
                  </div>
                </div>

                <div>
                  <span className="inline-flex items-center rounded-full bg-[#ee7d32]/10 border border-[#ee7d32]/20 px-3 py-1 text-[11px] md:text-[12px] font-semibold uppercase tracking-widest text-[#ee7d32]">
                    Fluidos de completamiento
                  </span>

                  <h3 className="mt-3 text-2xl sm:text-3xl font-bold text-[#111] leading-tight">
                    ¿Necesitas una solución para tu operación?
                  </h3>

                  <p className="mt-4 text-sm md:text-base leading-7 text-[#6b7280] max-w-2xl">
                    Diseñamos sistemas de fluidos de completamiento a la medida,
                    integrando tecnología, ingeniería y soporte especializado
                    para cada pozo y reservorio.
                  </p>
                </div>
              </div>

              <div className="flex w-full lg:w-auto">
                <Link
                  to="/contact-us?division=oilgas&service=Fluidos de completamiento"
                  className="group/btn inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-lg bg-[#ee7d32] px-6 py-3.5 md:px-7 md:py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#e56f20] hover:shadow-lg hover:shadow-[#ee7d32]/20"
                >
                  Contáctanos
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover/btn:translate-x-1"
                    color="white"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CompletionFluids;
